import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Overline } from "@/components/ui-bits";
import { toast } from "sonner";
import {
  Target, Loader2, Plug, Trash2, Check, MapPin, Mail, Phone, ExternalLink, Copy, Download,
  Sparkles, X, Building2, Star, Send, Rocket, Clock, Zap, Power, ShieldAlert, Flame, Gauge, ArrowUpDown, TrendingUp, Edit3, Bell,
} from "lucide-react";

const OFFERS = [
  { key: "landing_social", label: "Landing page + social campaign" },
  { key: "landing_page", label: "Landing page only" },
];

export default function Outreach() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [due, setDue] = useState(null);
  const [hot, setHot] = useState(null);
  const [ab, setAb] = useState(null);
  const pollRef = useRef(null);

  const load = () => {
    api.get("/outreach/config").then((r) => setCfg(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/outreach/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/outreach/followups-due").then((r) => setDue(r.data)).catch(() => {});
    api.get("/outreach/hot-leads").then((r) => setHot(r.data)).catch(() => {});
    api.get("/outreach/ab-stats").then((r) => setAb(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Poll while any campaign is running/queued
  useEffect(() => {
    const active = (cfg?.campaigns || []).some((c) => ["queued", "running"].includes(c.status));
    if (active && !pollRef.current) {
      pollRef.current = setInterval(() => { load(); if (detail) refreshDetail(detail.campaign.id); }, 4000);
    }
    if (!active && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current && !active) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [cfg, detail]);

  const refreshDetail = (cid) => api.get(`/outreach/campaigns/${cid}`).then((r) => setDetail(r.data)).catch(() => {});

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;

  return (
    <div className="space-y-6" data-testid="outreach-page">
      <div>
        <Overline>Direct Outreach</Overline>
        <h1 className="font-display text-3xl font-extrabold text-slate-100 mt-1">Local Outreach Engine</h1>
        <p className="text-slate-400 text-sm mt-1 max-w-2xl">Find local businesses by industry + location, auto-draft a personalized pitch, and build them a live demo landing page — then approve a campaign for <span className="text-amber-300">auto-send</span> and let the sequence email itself, or export and send from your own inbox.</p>
      </div>

      <PlacesConnect cfg={cfg} onChange={load} />

      {hot?.count > 0 && <HotLeads data={hot} ab={ab} onChange={load} />}

      {cfg?.connected && <CampaignCreator cfg={cfg} onChange={load} />}

      {cfg?.connected && <AutoSendSettings cfg={cfg} onChange={load} />}

      <OutreachAnalytics data={analytics} />

      <FollowUpsDue data={due} onChange={load} />

      <CampaignList cfg={cfg} onOpen={refreshDetail} onChange={load} />

      {detail && <ProspectDrawer detail={detail} onClose={() => setDetail(null)} onChange={() => refreshDetail(detail.campaign.id)} />}
    </div>
  );
}

function AbStatsCard({ ab }) {
  if (!ab) return null;
  const v = ab.variants || {};
  const labels = { diagnostic: "Diagnostic", opportunity: "Opportunity" };
  const rows = ["diagnostic", "opportunity"].map((k) => ({ key: k, ...(v[k] || { sent: 0, replied: 0, reply_rate: 0 }) }));
  const anySent = rows.some((r) => r.sent > 0);
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4" data-testid="ab-stats">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5 text-cyan-400" /><span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Pitch-line A/B test</span></div>
        {ab.leader && (
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${ab.locked ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : "bg-slate-800 text-slate-400 border border-slate-700"}`}>
            {ab.locked ? "Winner locked" : "Leading"}: {labels[ab.leader]}
          </span>
        )}
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => {
          const pct = Math.round((r.reply_rate || 0) * 100);
          const lead = ab.leader === r.key;
          return (
            <div key={r.key} data-testid={`ab-row-${r.key}`}>
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className={`font-semibold ${lead ? "text-cyan-300" : "text-slate-300"}`}>{labels[r.key]}</span>
                <span className="text-slate-500 font-mono">{r.replied}/{r.sent} replied · {pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: lead ? "#22D3EE" : "#475569" }} />
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-500 mt-3">{anySent ? `Auto-favors the winner once each style has ${ab.min_sample}+ sends; splitting 50/50 until then.` : "The engine alternates both styles on new campaigns, then favors whichever books more replies."}</p>
    </div>
  );
}

function OppBadge({ score }) {
  const color = score >= 70 ? "#F43F5E" : score >= 40 ? "#F59E0B" : "#10B981";
  return (
    <div className="flex items-center gap-2 min-w-[112px]">
      <div className="h-2 flex-1 rounded-full bg-slate-800 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${score}%`, background: color }} />
      </div>
      <span className="text-xs font-bold tabular-nums" style={{ color }}>{score}</span>
    </div>
  );
}

function BulkPitchReview({ ids, onClose, onSent }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post("/outreach/pitches", { ids }).then((r) => setItems((r.data.pitches || []).map((p) => ({ ...p, dirty: false }))))
      .catch(() => toast.error("Could not load pitches"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (i, f, v) => setItems((prev) => prev.map((x, idx) => (idx === i ? { ...x, [f]: v, dirty: true } : x)));
  const sendAll = async () => {
    setBusy(true);
    try {
      for (const it of items) { if (it.dirty) await api.patch(`/outreach/prospects/${it.id}/sequence`, { email_subject: it.subject, email_body: it.body }); }
      const r = await api.post("/outreach/bulk-pitch", { ids: items.map((x) => x.id) });
      const { sent, skipped } = r.data;
      if (sent) toast.success(`Sent ${sent} pitch${sent > 1 ? "es" : ""} 🚀${skipped?.length ? ` · ${skipped.length} skipped` : ""}`);
      else toast.message(`No pitches sent — ${skipped?.[0]?.reason || "unknown"}`);
      onSent && onSent();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send"); }
    finally { setBusy(false); }
  };
  const pendCount = (items || []).filter((i) => !i.pitched).length;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="bulk-review-modal">
      <div className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <div className="flex items-center gap-2"><Send className="w-4 h-4 text-cyan-400" /><h3 className="font-display text-lg font-bold text-slate-100">Review {items?.length || 0} pitch{(items?.length || 0) !== 1 ? "es" : ""}</h3></div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!items ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div> : items.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No pitches to review.</p>
          ) : items.map((it, i) => (
            <div key={it.id} data-testid={`review-item-${it.id}`} className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-bold text-slate-200">{it.business_name} <span className="text-[11px] font-normal text-slate-500 font-mono">· {it.email || "no email"}</span></div>
                {it.pitched && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300/80">Pitched</span>}
              </div>
              <input data-testid={`review-subject-${it.id}`} value={it.subject} onChange={(e) => update(i, "subject", e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 mb-2" placeholder="Subject" />
              <textarea data-testid={`review-body-${it.id}`} value={it.body} onChange={(e) => update(i, "body", e.target.value)} rows={5}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 leading-relaxed focus:outline-none focus:ring-1 focus:ring-cyan-500/40 resize-y" placeholder="Email body" />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 p-5 border-t border-slate-800">
          <span className="text-[11px] text-slate-500">{pendCount} ready to send · edits auto-save on send</span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-xs font-semibold text-slate-400 border border-slate-700 rounded-md px-3 py-2 hover:bg-slate-800">Cancel</button>
            <button data-testid="review-send-all" onClick={sendAll} disabled={busy || !items?.length}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-950 bg-cyan-400 hover:bg-cyan-300 rounded-md px-4 py-2 disabled:opacity-40">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send all ({items?.length || 0})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PitchPreview({ pid, onClose, onSent }) {
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    api.get(`/outreach/prospects/${pid}/pitch`).then((r) => {
      setData(r.data); setSubject(r.data.subject || ""); setBody(r.data.body || "");
    }).catch(() => toast.error("Could not load pitch"));
  }, [pid]);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/outreach/prospects/${pid}/sequence`, { email_subject: subject, email_body: body }); setDirty(false); toast.success("Pitch saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      if (dirty) await api.patch(`/outreach/prospects/${pid}/sequence`, { email_subject: subject, email_body: body });
      const r = await api.post("/outreach/bulk-pitch", { ids: [pid] });
      if (r.data.sent) toast.success("Pitch sent 🚀");
      else toast.message(`Not sent — ${r.data.skipped?.[0]?.reason || "unknown"}`);
      onSent && onSent();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="pitch-preview-modal">
      <div className="w-full max-w-lg bg-slate-950 border border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Edit3 className="w-4 h-4 text-cyan-400" /><h3 className="font-display text-lg font-bold text-slate-100">Pitch preview</h3></div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        {!data ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div> : (
          <>
            <div className="text-[11px] text-slate-500 mb-3">To <span className="text-slate-300 font-mono">{data.email || "(no email)"}</span> · {data.business_name}{data.pitched && <span className="ml-2 text-cyan-300">· already pitched</span>}</div>
            <label className="block text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-1">Subject</label>
            <input data-testid="pitch-subject" value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true); }}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 mb-3" />
            <label className="block text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-1">Email body</label>
            <textarea data-testid="pitch-body" value={body} onChange={(e) => { setBody(e.target.value); setDirty(true); }} rows={9}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 leading-relaxed focus:outline-none focus:ring-2 focus:ring-cyan-500/40 resize-y" />
            <p className="text-[10px] text-slate-500 mt-1">Links like the demo preview are inserted automatically when sent.</p>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button data-testid="pitch-save" onClick={save} disabled={busy || !dirty}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 border border-slate-700 rounded-md px-3 py-2 hover:bg-slate-800 disabled:opacity-40">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
              </button>
              <button data-testid="pitch-send" onClick={send} disabled={busy || data.pitched || !data.email || !body.trim()}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-950 bg-cyan-400 hover:bg-cyan-300 rounded-md px-3 py-2 disabled:opacity-40">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {data.pitched ? "Already sent" : "Send this pitch"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function HotLeads({ data, ab, onChange }) {
  const [sort, setSort] = useState("opportunity");
  const [sel, setSel] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const leads = [...(data.leads || [])];
  if (sort === "replied") {
    const rank = { replied: 0, claimed: 1, new: 2 };
    leads.sort((a, b) => (rank[a.status] - rank[b.status]) || (b.opportunity - a.opportunity));
  } else {
    leads.sort((a, b) => b.opportunity - a.opportunity);
  }
  const pitchable = leads.filter((l) => l.has_email && l.status === "new" && !l.pitched);
  const toggle = (id) => setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = pitchable.length > 0 && pitchable.every((l) => sel.has(l.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(pitchable.map((l) => l.id)));
  const sendPitch = async () => {
    const ids = [...sel];
    if (!ids.length) return;
    setSending(true);
    try {
      const r = await api.post("/outreach/bulk-pitch", { ids });
      const { sent, skipped } = r.data;
      if (sent) toast.success(`Pitched ${sent} lead${sent > 1 ? "s" : ""} 🚀${skipped?.length ? ` · ${skipped.length} skipped` : ""}`);
      else toast.message(`No emails sent${skipped?.length ? ` — ${skipped[0].reason}` : ""}`);
      setSel(new Set());
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send pitches");
    } finally { setSending(false); }
  };
  const statusPill = (s) => {
    const map = { new: ["New", "bg-slate-800 text-slate-300"], replied: ["Replied", "bg-emerald-500/15 text-emerald-300"], claimed: ["Claimed", "bg-cyan-500/15 text-cyan-300"] };
    const [t, c] = map[s] || map.new;
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c}`}>{t}</span>;
  };
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5" data-testid="hot-leads">
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-rose-400" />
              <h2 className="font-display text-lg font-bold text-slate-100">Hottest Leads</h2>
              <span className="text-[11px] text-slate-500">· weakest sites first</span>
            </div>
            <div className="flex items-center gap-2">
              {sel.size > 0 && (
                <>
                  <button data-testid="bulk-review-btn" onClick={() => setReviewOpen(true)}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-cyan-300 border border-cyan-500/40 bg-cyan-500/10 rounded-md px-2.5 py-1 hover:bg-cyan-500/20">
                    <Edit3 className="w-3 h-3" /> Review ({sel.size})
                  </button>
                  <button data-testid="bulk-pitch-btn" onClick={sendPitch} disabled={sending}
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-950 bg-cyan-400 hover:bg-cyan-300 rounded-md px-2.5 py-1 disabled:opacity-60">
                    {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send pitch ({sel.size})
                  </button>
                </>
              )}
              <button data-testid="hot-sort-toggle" onClick={() => setSort(sort === "opportunity" ? "replied" : "opportunity")}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 hover:text-slate-200 border border-slate-700 rounded-md px-2 py-1">
                <ArrowUpDown className="w-3 h-3" /> {sort === "opportunity" ? "Opportunity" : "Reply status"}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left">
                  <th className="py-1.5 px-1 font-mono font-medium">
                    <input type="checkbox" data-testid="hot-select-all" checked={allSelected} onChange={toggleAll} disabled={!pitchable.length}
                      className="accent-cyan-400 cursor-pointer disabled:opacity-30" title="Select all pitchable leads" />
                  </th>
                  <th className="py-1.5 px-1 font-mono font-medium">Opportunity</th>
                  <th className="py-1.5 px-1 font-mono font-medium">Business</th>
                  <th className="py-1.5 px-1 font-mono font-medium hidden md:table-cell">Signals</th>
                  <th className="py-1.5 px-1 font-mono font-medium">Email</th>
                  <th className="py-1.5 px-1 font-mono font-medium">Status</th>
                  <th className="py-1.5 px-1 font-mono font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => {
                  const canPitch = l.has_email && l.status === "new" && !l.pitched;
                  return (
                  <tr key={l.id} data-testid={`hot-lead-${l.id}`} className={`border-t border-slate-800/70 hover:bg-slate-800/30 ${sel.has(l.id) ? "bg-cyan-500/5" : ""}`}>
                    <td className="py-2 px-1">
                      <input type="checkbox" data-testid={`hot-select-${l.id}`} checked={sel.has(l.id)} onChange={() => toggle(l.id)} disabled={!canPitch}
                        className="accent-cyan-400 cursor-pointer disabled:opacity-20" title={canPitch ? "Select" : "Already pitched, replied, or no email"} />
                    </td>
                    <td className="py-2 px-1"><OppBadge score={l.opportunity} /></td>
                    <td className="py-2 px-1">
                      <div className="font-semibold text-slate-200 truncate max-w-[160px]">{l.business_name}</div>
                      <div className="text-[10px] text-slate-500 truncate max-w-[160px]">{l.vertical || l.city || ""}</div>
                    </td>
                    <td className="py-2 px-1 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {l.speed_score != null && <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded ${l.speed_score < 50 ? "bg-rose-500/15 text-rose-300" : "bg-slate-800 text-slate-400"}`}><Gauge className="w-2.5 h-2.5" />{l.speed_score}</span>}
                        {l.platform && <span className={`text-[10px] px-1.5 py-0.5 rounded ${l.dated_builder ? "bg-amber-500/15 text-amber-300" : "bg-slate-800 text-slate-400"}`}>{l.platform}</span>}
                        {l.review_count != null && <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400`}><Star className="w-2.5 h-2.5" />{l.rating || "—"}·{l.review_count}</span>}
                      </div>
                    </td>
                    <td className="py-2 px-1">{l.has_email ? <Mail className="w-3.5 h-3.5 text-emerald-400" /> : <span className="text-slate-600 text-xs">—</span>}</td>
                    <td className="py-2 px-1">{l.pitched && l.status === "new" ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300/80">Pitched</span> : statusPill(l.status)}</td>
                    <td className="py-2 px-1 text-right whitespace-nowrap">
                      <button data-testid={`hot-editpitch-${l.id}`} onClick={() => setPreviewId(l.id)} title="Preview / edit pitch"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-cyan-300 mr-2"><Edit3 className="w-3 h-3" /> Pitch</button>
                      {l.preview_url && <a href={l.preview_url} target="_blank" rel="noreferrer" data-testid={`hot-preview-${l.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-cyan-400 hover:text-cyan-300"><ExternalLink className="w-3 h-3" /> Preview</a>}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div><AbStatsCard ab={ab} /></div>
      </div>
      {previewId && <PitchPreview pid={previewId} onClose={() => setPreviewId(null)} onSent={() => { setPreviewId(null); onChange && onChange(); }} />}
      {reviewOpen && <BulkPitchReview ids={[...sel]} onClose={() => setReviewOpen(false)} onSent={() => { setReviewOpen(false); setSel(new Set()); onChange && onChange(); }} />}
    </div>
  );
}

function PlacesConnect({ cfg, onChange }) {
  const connected = cfg?.connected;
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try { await api.post("/outreach/connect", { google_places_api_key: key }); toast.success("Google Places connected"); setOpen(false); setKey(""); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not connect"); } finally { setBusy(false); }
  };
  const disconnect = async () => { if (!window.confirm("Disconnect Google Places?")) return; await api.delete("/outreach/disconnect"); toast.success("Disconnected"); onChange(); };

  return (
    <div className="hud-card rounded-lg p-5" data-testid="places-connect-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-rose-400" />
          <span className="font-display font-bold text-rose-300">Google Places</span>
          <span className="text-[11px] text-slate-500">business discovery source</span>
        </div>
        {connected ? <span className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px]">CONNECTED</span>
          : <span className="px-2 py-0.5 rounded bg-slate-700/40 text-slate-400 font-mono text-[10px]">NOT CONNECTED</span>}
      </div>
      {connected ? (
        <button data-testid="places-disconnect-btn" onClick={disconnect} className="flex items-center gap-1.5 text-[11px] font-mono text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /> Disconnect key</button>
      ) : !open ? (
        <button data-testid="places-connect-btn" onClick={() => setOpen(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-rose-500/15 border border-rose-500/40 text-rose-300 font-mono text-xs uppercase tracking-wider hover:bg-rose-500/25"><Plug className="w-4 h-4" /> Connect Google Places key</button>
      ) : (
        <div className="space-y-2.5">
          <input data-testid="places-key-input" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="AIza… (Places API New key)" className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-rose-500/60 focus:outline-none" />
          <div className="text-[10px] text-slate-500 leading-relaxed rounded-md bg-slate-900/50 border border-slate-800 p-2.5">
            In <b className="text-slate-400">Google Cloud Console</b> → create a project with billing → enable <span className="text-rose-300">Places API (New)</span> → Credentials → create an API key → restrict it to Places API (New). Paste it here (stored server-side only, never shown again).
          </div>
          <div className="flex gap-2">
            <button data-testid="places-connect-submit" onClick={connect} disabled={busy || !key} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-md bg-rose-500 text-[#080B11] font-bold text-sm hover:bg-rose-400 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Verify &amp; connect</button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-slate-700 text-slate-400 text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CampaignCreator({ cfg, onChange }) {
  const [industry, setIndustry] = useState("");
  const [location, setLocation] = useState("");
  const [max, setMax] = useState(20);
  const [offer, setOffer] = useState("landing_social");
  const [buildDemo, setBuildDemo] = useState(true);
  const [tz, setTz] = useState(cfg?.window?.default_tz || "America/New_York");
  const [busy, setBusy] = useState(false);

  const launch = async () => {
    if (!industry.trim() || !location.trim()) { toast.error("Enter an industry and a location"); return; }
    setBusy(true);
    try {
      await api.post("/outreach/campaigns", { industry, location, max_results: Number(max), offer_type: offer, build_demo: buildDemo, timezone: tz });
      toast.success("Outreach launched — finding businesses & drafting emails…");
      setIndustry(""); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start"); } finally { setBusy(false); }
  };

  return (
    <div className="hud-card rounded-lg p-5" data-testid="campaign-creator">
      <div className="flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4 text-cyan-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">New outreach campaign</span></div>
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="vertical-chips">
        {(cfg?.verticals || []).map((v) => (
          <button key={v} data-testid={`vertical-${v.replace(/[^a-z0-9]+/gi, "-")}`} onClick={() => setIndustry(v)} className={`px-2 py-0.5 rounded-full text-[10px] font-mono border transition-colors ${industry === v ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/50" : "bg-slate-800/40 text-slate-400 border-slate-700 hover:border-cyan-500/50 hover:text-cyan-300"}`}>{v}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Industry / business type</label>
          <input data-testid="industry-input" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Dentists" className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Location (city / zip)</label>
          <input data-testid="location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Austin, TX 78701" className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Max businesses ({max})</label>
          <input data-testid="max-input" type="range" min="5" max="60" step="5" value={max} onChange={(e) => setMax(e.target.value)} className="w-full accent-cyan-500" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Offer</label>
          <select data-testid="offer-select" value={offer} onChange={(e) => setOffer(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none">
            {OFFERS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Prospect timezone (for send windows)</label>
          <select data-testid="timezone-select" value={tz} onChange={(e) => setTz(e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none">
            {(cfg?.timezones || ["America/New_York"]).map((z) => <option key={z} value={z}>{z.replace("_", " ")}</option>)}
          </select>
        </div>
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm text-slate-300 cursor-pointer">
        <input data-testid="build-demo-toggle" type="checkbox" checked={buildDemo} onChange={(e) => setBuildDemo(e.target.checked)} className="accent-cyan-500 w-4 h-4" />
        Auto-build a demo landing page for each business (linked in the email)
      </label>
      <button data-testid="launch-outreach-btn" onClick={launch} disabled={busy} className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Launch outreach
      </button>
    </div>
  );
}

function FollowUpsDue({ data, onChange }) {
  if (!data || (!data.count && !(data.upcoming || []).length)) return null;
  const gmail = (d) => `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(d.email || "")}&su=${encodeURIComponent(d.subject || "")}&body=${encodeURIComponent(d.body || "")}`;
  const markSent = async (id) => { try { await api.post(`/outreach/prospects/${id}/sent`); toast.success("Marked sent — next touch scheduled"); onChange(); } catch { toast.error("Could not update"); } };
  const schedule = async (id, date) => { try { await api.post(`/outreach/prospects/${id}/schedule`, { date }); toast.success(date ? "Scheduled" : "Schedule cleared"); onChange(); } catch { toast.error("Could not schedule"); } };
  return (
    <div className="hud-card rounded-lg p-5" data-testid="followups-due-card">
      <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-amber-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-amber-400/90">Follow-ups due</span><span className="px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-400 font-mono text-[10px]">{data.count}</span></div>
      <p className="text-[10px] text-slate-500 mb-3">No reply yet — send the next touch (day 3 → #2, day 7 → #3), or pick a future date to schedule it. Mark sent to advance.</p>
      <div className="space-y-2">
        {(data.due || []).map((d) => (
          <div key={d.id} data-testid={`due-${d.id}`} className="flex items-center justify-between gap-3 rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2 flex-wrap">
            <div className="min-w-0">
              <div className="text-sm text-slate-200 truncate">{d.business_name} <span className="text-[10px] font-mono text-amber-400">· touch #{d.touch}</span></div>
              <div className="text-[10px] font-mono text-slate-500">{d.email} · {d.age_days}d overdue · {d.industry}</div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <input type="date" data-testid={`due-date-${d.id}`} defaultValue={(d.scheduled_at || "").slice(0, 10)} onChange={(e) => schedule(d.id, e.target.value)} className="bg-slate-900/60 border border-slate-700 rounded px-1.5 py-1 text-[11px] text-slate-300" />
              <a data-testid={`due-gmail-${d.id}`} href={gmail(d)} target="_blank" rel="noreferrer" className="px-2.5 py-1 rounded bg-red-500/15 border border-red-500/40 text-red-300 text-[11px] inline-flex items-center gap-1 hover:bg-red-500/25"><Send className="w-3 h-3" /> Gmail</a>
              <button data-testid={`due-sent-${d.id}`} onClick={() => markSent(d.id)} className="px-2.5 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[11px] hover:bg-emerald-500/20">Mark sent</button>
            </div>
          </div>
        ))}
      </div>
      {(data.upcoming || []).length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">Upcoming ({data.upcoming.length})</div>
          <div className="space-y-1.5">
            {data.upcoming.map((d) => (
              <div key={d.id} data-testid={`upcoming-${d.id}`} className="flex items-center justify-between gap-3 rounded-md border border-slate-800/60 bg-slate-900/20 px-3 py-1.5">
                <div className="text-[12px] text-slate-300 truncate">{d.business_name} <span className="text-[10px] font-mono text-slate-500">· touch #{d.touch}</span></div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] font-mono text-cyan-400">{(d.scheduled_at || "").slice(0, 10)} · in {d.days_until}d</span>
                  <button data-testid={`upcoming-clear-${d.id}`} onClick={() => schedule(d.id, "")} className="text-[10px] font-mono text-slate-500 hover:text-red-400">clear</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OutreachAnalytics({ data }) {
  if (!data || !data.overall?.prospects) return null;
  const o = data.overall;
  const Stat = ({ label, value, accent }) => (
    <div className="rounded-md border border-slate-800 bg-slate-900/40 px-4 py-3">
      <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display text-2xl font-extrabold mt-0.5 ${accent || "text-slate-100"}`}>{value}</div>
    </div>
  );
  return (
    <div className="hud-card rounded-lg p-5" data-testid="outreach-analytics">
      <div className="flex items-center gap-2 mb-4"><Target className="w-4 h-4 text-emerald-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Outreach analytics</span>
        {data.best_vertical && <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded bg-violet-500/10 border border-violet-500/30 text-violet-300 font-mono text-[10px]">top: {data.best_vertical}</span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        <Stat label="Prospects" value={o.prospects} />
        <Stat label="With email" value={o.emailed} />
        <Stat label="Replied" value={o.replied} accent="text-cyan-300" />
        <Stat label="Closed" value={o.closed} accent="text-emerald-400" />
        <Stat label="Close rate" value={`${o.close_rate}%`} accent="text-emerald-400" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[9px] font-mono uppercase tracking-wider text-slate-500 text-left border-b border-slate-800">
            <th className="py-2 pr-3">Vertical</th><th className="py-2 pr-3">Prospects</th><th className="py-2 pr-3">Replied</th><th className="py-2 pr-3">Closed</th><th className="py-2">Reply / Close</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-800/60">
            {data.by_vertical.map((r) => (
              <tr key={r.vertical} data-testid={`analytics-row-${r.vertical.replace(/[^a-z0-9]+/gi, "-")}`}>
                <td className="py-2.5 pr-3 text-slate-200">{r.vertical}</td>
                <td className="py-2.5 pr-3 text-slate-300 font-mono">{r.prospects}</td>
                <td className="py-2.5 pr-3 text-cyan-300 font-mono">{r.replied}</td>
                <td className="py-2.5 pr-3 text-emerald-400 font-mono">{r.closed}</td>
                <td className="py-2.5 text-slate-400 font-mono text-xs">{r.reply_rate}% / {r.close_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ s }) {
  const map = { queued: "bg-slate-700/40 text-slate-400", running: "bg-amber-500/10 text-amber-400 border border-amber-500/30", complete: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30", failed: "bg-red-500/10 text-red-400 border border-red-500/30" };
  return <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${map[s] || map.queued}`}>{s}</span>;
}

function AutoSendSettings({ cfg, onChange }) {
  const [enabled, setEnabled] = useState(!!cfg?.auto_send_enabled);
  const [cap, setCap] = useState(cfg?.daily_send_cap || 25);
  const [busy, setBusy] = useState(false);
  const w = cfg?.window || {};
  const [winOn, setWinOn] = useState(!!w.enabled);
  const [wStart, setWStart] = useState(w.start ?? 9);
  const [wEnd, setWEnd] = useState(w.end ?? 17);
  const [weekdays, setWeekdays] = useState(w.weekdays_only !== false);
  const [autoQ, setAutoQ] = useState(cfg?.reply_autoqualify !== false);
  const [postSale, setPostSale] = useState(cfg?.post_sale_upsell_enabled !== false);
  const [alertOn, setAlertOn] = useState(cfg?.hot_lead_alert_enabled !== false);
  const [alertThresh, setAlertThresh] = useState(cfg?.hot_lead_alert_threshold ?? 70);
  const [alertEmail, setAlertEmail] = useState(cfg?.hot_lead_alert_email !== false);
  const [alertTg, setAlertTg] = useState(cfg?.hot_lead_alert_telegram !== false);
  const [alertMode, setAlertMode] = useState(cfg?.hot_lead_alert_mode || "hourly");
  useEffect(() => {
    setEnabled(!!cfg?.auto_send_enabled); setCap(cfg?.daily_send_cap || 25);
    const ww = cfg?.window || {};
    setWinOn(!!ww.enabled); setWStart(ww.start ?? 9); setWEnd(ww.end ?? 17); setWeekdays(ww.weekdays_only !== false);
    setAutoQ(cfg?.reply_autoqualify !== false);
    setPostSale(cfg?.post_sale_upsell_enabled !== false);
    setAlertOn(cfg?.hot_lead_alert_enabled !== false);
    setAlertThresh(cfg?.hot_lead_alert_threshold ?? 70);
    setAlertEmail(cfg?.hot_lead_alert_email !== false);
    setAlertTg(cfg?.hot_lead_alert_telegram !== false);
    setAlertMode(cfg?.hot_lead_alert_mode || "hourly");
  }, [cfg]);

  const approved = (cfg?.campaigns || []).filter((c) => c.auto_send).length;
  const save = async (next, msg) => {
    try { await api.patch("/outreach/settings", next); if (msg) toast.success(msg); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
  };
  const toggle = async () => { const v = !enabled; setEnabled(v); await save({ outreach_auto_send_enabled: v }, v ? "Auto-send ON — approved campaigns will send on schedule" : "Auto-send paused"); };
  const saveCap = async () => { await save({ daily_send_cap: Number(cap) }, "Daily cap saved"); };
  const toggleWin = async () => { const v = !winOn; setWinOn(v); await save({ window_enabled: v }, v ? "Send window on" : "Send window off — sends anytime"); };
  const saveWin = async () => { await save({ window_start: Number(wStart), window_end: Number(wEnd), window_weekdays_only: weekdays }, "Send window saved"); };
  const toggleAutoQ = async () => { const v = !autoQ; setAutoQ(v); await save({ reply_autoqualify: v }, v ? "Reply auto-qualify on" : "Reply auto-qualify off"); };
  const togglePostSale = async () => { const v = !postSale; setPostSale(v); await save({ post_sale_upsell_enabled: v }, v ? "Post-sale upsells on" : "Post-sale upsells off"); };
  const toggleAlert = async () => { const v = !alertOn; setAlertOn(v); await save({ hot_lead_alert_enabled: v }, v ? "Hot-lead alerts on" : "Hot-lead alerts off"); };
  const saveThresh = async () => { await save({ hot_lead_alert_threshold: Number(alertThresh) }, `Alert threshold set to ${alertThresh}`); };
  const toggleAlertEmail = async () => { const v = !alertEmail; setAlertEmail(v); await save({ hot_lead_alert_email: v }); };
  const toggleAlertTg = async () => { const v = !alertTg; setAlertTg(v); await save({ hot_lead_alert_telegram: v }); };
  const setMode = async (m) => { setAlertMode(m); await save({ hot_lead_alert_mode: m }, m === "instant" ? "Instant alerts" : "Hourly digest"); };
  const runNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/outreach/autosend/run"); toast.success(data.started ? "Sending approved touches now…" : "Already running"); setTimeout(onChange, 2500); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not run"); }
    finally { setBusy(false); }
  };
  const hourLabel = (h) => `${((h % 12) || 12)}${h < 12 ? "am" : "pm"}`;

  return (
    <div className="hud-card rounded-lg p-5" data-testid="autosend-settings">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-400" />
          <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Auto-Send Sequences</span>
        </div>
        <button
          data-testid="autosend-master-toggle"
          onClick={toggle}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${enabled ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200"}`}
        >
          <Power className="w-3.5 h-3.5" /> {enabled ? "Auto-send ON" : "Auto-send OFF"}
        </button>
      </div>
      <p className="text-[11px] text-slate-500 mt-2 max-w-2xl">Approved campaigns send their initial pitch and follow-ups (day 3 → #2, day 7 → #3) automatically via email — no manual Gmail drafts. Sending stops the instant a prospect replies. Replies still land in your Inbox.</p>

      <div className="flex items-center gap-4 mt-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Daily send cap</label>
          <input data-testid="autosend-cap-input" type="number" min="1" max="200" value={cap} onChange={(e) => setCap(e.target.value)} onBlur={saveCap} className="w-20 bg-slate-900/60 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200" />
        </div>
        <div className="text-[11px] font-mono text-slate-500">Sent today: <span className="text-slate-300">{cfg?.sent_today ?? 0}</span> / {cfg?.daily_send_cap ?? 25}</div>
        <div className="text-[11px] font-mono text-slate-500">Approved campaigns: <span className={approved ? "text-amber-300" : "text-slate-400"}>{approved}</span></div>
        <button data-testid="autosend-run-now" onClick={runNow} disabled={busy} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 text-xs hover:bg-cyan-500/20 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send now
        </button>
      </div>

      {/* Send Windows */}
      <div className="mt-4 pt-4 border-t border-slate-800">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-slate-400" /><span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Send window (prospect's timezone)</span></div>
          <button data-testid="window-toggle" onClick={toggleWin} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${winOn ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200"}`}>
            <Power className="w-3 h-3" /> {winOn ? "Window ON" : "Window OFF"}
          </button>
        </div>
        {winOn && (
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">From</label>
              <select data-testid="window-start" value={wStart} onChange={(e) => setWStart(e.target.value)} onBlur={saveWin} className="bg-slate-900/60 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200">
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
              <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">to</label>
              <select data-testid="window-end" value={wEnd} onChange={(e) => setWEnd(e.target.value)} onBlur={saveWin} className="bg-slate-900/60 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200">
                {Array.from({ length: 25 }, (_, h) => <option key={h} value={h}>{h === 24 ? "12am" : hourLabel(h)}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-[12px] text-slate-300 cursor-pointer">
              <input data-testid="window-weekdays" type="checkbox" checked={weekdays} onChange={(e) => { setWeekdays(e.target.checked); setTimeout(saveWin, 0); }} className="accent-cyan-500 w-4 h-4" />
              Weekdays only
            </label>
            <span className="text-[10px] font-mono text-slate-500">Each campaign uses its own timezone (set when created). Default: {w.default_tz || "America/New_York"}</span>
          </div>
        )}
      </div>

      {/* Reply Auto-Qualify */}
      <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-emerald-400" /><span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Reply Auto-Negotiator</span></div>
          <p className="text-[11px] text-slate-500 mt-1 max-w-xl">When a prospect replies, the seller agent classifies intent and auto-handles it by email: <strong className="text-slate-400">Interested</strong> → warm reply + one-tap pay links, <strong className="text-slate-400">Price</strong> → the options with a step-3 discount, <strong className="text-slate-400">Feature</strong> → a freshly-revised preview. Anything unclear or a "no thanks" is escalated to your Inbox.</p>
        </div>
        <button data-testid="autoqualify-toggle" onClick={toggleAutoQ} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${autoQ ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200"}`}>
          <Power className="w-3 h-3" /> {autoQ ? "Auto-negotiate ON" : "Auto-negotiate OFF"}
        </button>
      </div>

      {/* Post-Sale Upsells & Referrals */}
      <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-cyan-400" /><span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Post-Sale Upsells &amp; Referrals</span></div>
          <p className="text-[11px] text-slate-500 mt-1 max-w-xl">48h after a delivered site goes live, auto-email the customer add-ons (Local SEO, Google Business Profile, Ads) plus a two-sided referral link — they get $50 off, the referrer gets $50.</p>
        </div>
        <button data-testid="postsale-toggle" onClick={togglePostSale} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${postSale ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200"}`}>
          <Power className="w-3 h-3" /> {postSale ? "Upsells ON" : "Upsells OFF"}
        </button>
      </div>

      {/* Hot-Lead Alerts */}
      <div className="mt-4 pt-4 border-t border-slate-800" data-testid="hot-alert-settings">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2"><Bell className="w-3.5 h-3.5 text-rose-400" /><span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Hot-Lead Alerts</span></div>
            <p className="text-[11px] text-slate-500 mt-1 max-w-xl">The moment a freshly-audited prospect scores at/above your opportunity threshold, get pinged so you can pitch them first.</p>
          </div>
          <button data-testid="hot-alert-toggle" onClick={toggleAlert} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${alertOn ? "bg-rose-500/15 border-rose-500/40 text-rose-300" : "bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200"}`}>
            <Power className="w-3 h-3" /> {alertOn ? "Alerts ON" : "Alerts OFF"}
          </button>
        </div>
        {alertOn && (
          <div className="mt-3 flex items-center gap-4 flex-wrap">
            <div className="inline-flex rounded-md border border-slate-700 overflow-hidden" data-testid="hot-alert-mode">
              <button data-testid="hot-alert-mode-hourly" onClick={() => setMode("hourly")} className={`px-2.5 py-1 text-[11px] font-semibold ${alertMode === "hourly" ? "bg-rose-500/20 text-rose-300" : "text-slate-400 hover:text-slate-200"}`}>Hourly digest</button>
              <button data-testid="hot-alert-mode-instant" onClick={() => setMode("instant")} className={`px-2.5 py-1 text-[11px] font-semibold border-l border-slate-700 ${alertMode === "instant" ? "bg-rose-500/20 text-rose-300" : "text-slate-400 hover:text-slate-200"}`}>Instant</button>
            </div>
            <label className="flex items-center gap-2 text-[11px] text-slate-400">
              Threshold
              <input data-testid="hot-alert-threshold" type="number" min={0} max={100} value={alertThresh}
                onChange={(e) => setAlertThresh(e.target.value)} onBlur={saveThresh}
                className="w-16 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-slate-100 text-xs focus:outline-none focus:ring-1 focus:ring-rose-500/50" />
              <span className="text-slate-500">/100</span>
            </label>
            <button data-testid="hot-alert-email" onClick={toggleAlertEmail} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-semibold border ${alertEmail ? "bg-slate-800 border-slate-600 text-slate-200" : "bg-slate-900 border-slate-800 text-slate-500"}`}>
              <Mail className="w-3 h-3" /> Email {alertEmail ? "on" : "off"}
            </button>
            <button data-testid="hot-alert-telegram" onClick={toggleAlertTg} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-semibold border ${alertTg ? "bg-slate-800 border-slate-600 text-slate-200" : "bg-slate-900 border-slate-800 text-slate-500"}`}>
              <Send className="w-3 h-3" /> Telegram {alertTg ? "on" : "off"}{!cfg?.telegram_verified && alertTg ? " (connect first)" : ""}
            </button>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-start gap-1.5 text-[10px] text-amber-400/80 bg-amber-500/5 border border-amber-500/20 rounded px-2.5 py-2">
        <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" />
        <span>Only send to businesses you have a legitimate reason to contact. Emails include an opt-out line. High bounce/complaint rates can hurt your sending domain's reputation.</span>
      </div>
    </div>
  );
}

function CampaignList({ cfg, onOpen, onChange }) {
  const camps = cfg?.campaigns || [];
  const del = async (cid) => { if (!window.confirm("Delete this campaign and its prospects?")) return; await api.delete(`/outreach/campaigns/${cid}`); toast.success("Deleted"); onChange(); };
  const toggleAuto = async (c) => {
    const approve = !c.auto_send;
    if (approve && !window.confirm(`Approve auto-send for "${c.industry} · ${c.location}"?\n\nKleen-G will email these prospects the pitch + follow-ups automatically (within your daily cap). Make sure auto-send is turned ON above.`)) return;
    try { await api.post(`/outreach/campaigns/${c.id}/autosend`, { approve }); toast.success(approve ? "Approved for auto-send" : "Auto-send paused for this campaign"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update"); }
  };
  if (!camps.length) return null;
  return (
    <div className="hud-card rounded-lg p-5" data-testid="campaign-list">
      <div className="flex items-center gap-2 mb-3"><Target className="w-4 h-4 text-slate-400" /><span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Campaigns</span></div>
      <div className="space-y-2">
        {camps.map((c) => (
          <div key={c.id} data-testid={`campaign-${c.id}`} className="flex items-center justify-between gap-3 rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-200 truncate">{c.industry} · {c.location}</span>
                <StatusPill s={c.status} />
                {c.auto_send && <span className="px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 font-mono text-[9px] uppercase inline-flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" />auto</span>}
              </div>
              <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                {["queued", "running"].includes(c.status) ? `${c.processed || 0}/${c.found || c.max_results} processed` : `${c.processed || 0} prospects · ${c.with_email || 0} emails found`}
                {c.error ? <span className="text-red-400"> · {c.error}</span> : ""}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {["queued", "running"].includes(c.status) && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
              {c.status === "complete" && (c.with_email || 0) > 0 && (
                <button data-testid={`autosend-toggle-${c.id}`} onClick={() => toggleAuto(c)} title={c.auto_send ? "Pause auto-send" : "Approve auto-send"}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-semibold border transition-colors inline-flex items-center gap-1 ${c.auto_send ? "bg-amber-500/15 border-amber-500/40 text-amber-300 hover:bg-amber-500/25" : "border-slate-700 text-slate-300 hover:border-amber-500/50 hover:text-amber-300"}`}>
                  <Zap className="w-3 h-3" /> {c.auto_send ? "Auto-send on" : "Auto-send"}
                </button>
              )}
              <button data-testid={`open-campaign-${c.id}`} onClick={() => onOpen(c.id)} className="px-3 py-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 text-xs hover:bg-cyan-500/20">View</button>
              <a data-testid={`export-campaign-${c.id}`} href={`${api.defaults.baseURL}/outreach/campaigns/${c.id}/export`} className="p-1.5 rounded-md border border-slate-700 text-slate-400 hover:text-slate-200" title="Export CSV"><Download className="w-3.5 h-3.5" /></a>
              <button data-testid={`delete-campaign-${c.id}`} onClick={() => del(c.id)} className="p-1.5 rounded-md text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProspectDrawer({ detail, onClose, onChange }) {
  const { campaign, prospects } = detail;
  const [view, setView] = useState(null);
  const copyEmail = (p) => { navigator.clipboard.writeText(`Subject: ${p.email_subject}\n\n${p.email_body}`); toast.success("Email copied"); };
  const gmailUrl = (p) => `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(p.email || "")}&su=${encodeURIComponent(p.email_subject || "")}&body=${encodeURIComponent(p.email_body || "")}`;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose} data-testid="prospect-drawer">
      <div className="w-full max-w-2xl h-full bg-[#0A0E17] border-l border-slate-800 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0A0E17] border-b border-slate-800 p-4 flex items-center justify-between z-10">
          <div><div className="font-display font-bold text-slate-100">{campaign.industry} · {campaign.location}</div><div className="text-[10px] font-mono text-slate-500">{prospects.length} prospects · {prospects.filter((p) => p.email).length} with email</div></div>
          <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-2">
          {prospects.map((p) => (
            <div key={p.id} data-testid={`prospect-${p.id}`} className="rounded-md border border-slate-800 bg-slate-900/40 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-sm text-slate-200"><Building2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />{p.business_name}
                    {p.rating ? <span className="text-[10px] font-mono text-amber-400 inline-flex items-center gap-0.5"><Star className="w-2.5 h-2.5" />{p.rating}</span> : null}
                    {p.replied ? <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 font-mono text-[9px] uppercase">replied</span> : null}
                    {!p.replied && p.auto_done ? <span className="px-1.5 py-0.5 rounded bg-slate-600/20 border border-slate-600/40 text-slate-400 font-mono text-[9px] uppercase">sequence done</span> : null}
                    {!p.replied && !p.auto_done && (p.auto_touch || 0) > 0 ? <span className="px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 font-mono text-[9px] uppercase inline-flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" />sent {p.auto_touch}/3</span> : null}
                  </div>
                  <div className="text-[11px] font-mono text-slate-500 mt-1 space-y-0.5">
                    {p.email ? <div className="flex items-center gap-1 text-emerald-400"><Mail className="w-3 h-3" />{p.email}</div> : <div className="flex items-center gap-1 text-slate-600"><Mail className="w-3 h-3" />no email found</div>}
                    {p.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</div>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  {p.preview_url && <a data-testid={`preview-link-${p.id}`} href={p.preview_url} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-cyan-400 inline-flex items-center gap-0.5 hover:text-cyan-300">demo <ExternalLink className="w-2.5 h-2.5" /></a>}
                  <div className="flex items-center gap-1">
                    {p.email && <a data-testid={`gmail-${p.id}`} href={gmailUrl(p)} target="_blank" rel="noreferrer" title="Open in Gmail (prefilled)" className="px-2 py-1 rounded bg-red-500/15 border border-red-500/40 text-red-300 text-[11px] inline-flex items-center gap-1 hover:bg-red-500/25"><Send className="w-3 h-3" /> Gmail</a>}
                    <button data-testid={`view-email-${p.id}`} onClick={() => setView(p)} className="px-2 py-1 rounded border border-slate-700 text-slate-300 text-[11px] hover:border-cyan-500/50">Email</button>
                    <button data-testid={`copy-email-${p.id}`} onClick={() => copyEmail(p)} className="p-1 rounded border border-slate-700 text-slate-400 hover:text-slate-200"><Copy className="w-3 h-3" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {!prospects.length && <div className="text-center text-slate-500 text-sm py-8">No prospects yet — still working…</div>}
        </div>
      </div>

      {view && (
        <SequenceEditor view={view} onClose={() => setView(null)} onSaved={onChange} />
      )}
    </div>
  );
}

function SequenceEditor({ view, onClose, onSaved }) {
  const [subject, setSubject] = useState(view.email_subject || "");
  const [body, setBody] = useState(view.email_body || "");
  const [fups, setFups] = useState((view.followups || []).map((f) => ({ subject: f.subject || "", body: f.body || "" })));
  const [busy, setBusy] = useState(false);
  const setFup = (i, k, v) => setFups((arr) => arr.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const gmailUrlFor = (email, s, b) => `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email || "")}&su=${encodeURIComponent(s || "")}&body=${encodeURIComponent(b || "")}`;
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/outreach/prospects/${view.id}/sequence`, { email_subject: subject, email_body: body, followups: fups });
      toast.success("Sequence saved");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };
  const touches = [{ label: "Initial pitch", subject, body, set: (k, v) => (k === "subject" ? setSubject(v) : setBody(v)) },
    ...fups.map((f, i) => ({ label: `Follow-up ${i + 1}`, subject: f.subject, body: f.body, set: (k, v) => setFup(i, k, v) }))];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#0A0E17] border border-slate-800 rounded-lg p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="email-modal">
        <div className="flex items-center justify-between mb-1"><span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Edit sequence · {view.business_name}</span><button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X className="w-4 h-4" /></button></div>
        <p className="text-[10px] text-slate-500 mb-3">Tweak the pitch and each follow-up before approving auto-send. Placeholders like the preview link are already filled in.</p>
        <div className="space-y-4">
          {touches.map((t, i) => (
            <div key={i} className="rounded-md border border-slate-800 bg-slate-900/40 p-3" data-testid={`touch-${i}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${i === 0 ? "bg-cyan-500/15 text-cyan-300" : "bg-slate-700/40 text-slate-400"}`}>{t.label}</span>
                {view.email && <a href={gmailUrlFor(view.email, t.subject, t.body)} target="_blank" rel="noreferrer" className="px-2 py-0.5 rounded bg-red-500/15 border border-red-500/40 text-red-300 text-[10px] inline-flex items-center gap-1 hover:bg-red-500/25"><Send className="w-2.5 h-2.5" /> Gmail</a>}
              </div>
              <input data-testid={`edit-subject-${i}`} value={t.subject} onChange={(e) => t.set("subject", e.target.value)} placeholder="Subject" className="w-full bg-slate-950/60 border border-slate-700 rounded px-2.5 py-1.5 text-sm text-slate-100 font-medium mb-2 focus:border-cyan-500/60 focus:outline-none" />
              <textarea data-testid={`edit-body-${i}`} value={t.body} onChange={(e) => t.set("body", e.target.value)} rows={5} placeholder="Body" className="w-full bg-slate-950/60 border border-slate-700 rounded px-2.5 py-1.5 text-[13px] text-slate-300 font-sans resize-y focus:border-cyan-500/60 focus:outline-none" />
            </div>
          ))}
        </div>
        {view.reply_to_alias && (
          <div className="mt-3 text-[10px] text-slate-500 leading-relaxed rounded-md bg-slate-900/50 border border-slate-800 p-2.5" data-testid="reply-capture-note">
            <b className="text-cyan-400">Replies auto-capture</b> to <span className="font-mono text-slate-300">{view.reply_to_alias}</span> → land in your Inbox for the seller agent.
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 text-xs hover:border-slate-500">Cancel</button>
          <button data-testid="save-sequence-btn" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-xs hover:bg-cyan-400 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save sequence
          </button>
        </div>
      </div>
    </div>
  );
}
